import React from 'react';
import { Carousel, type CarouselApi } from './carousel';
import { PlatformService } from '@lightning/services';
import { cn } from '@lightning/utils';

interface MobileCarouselProps extends React.ComponentProps<typeof Carousel> {
  mobileOpts?: any;
  desktopOpts?: any;
}

/**
 * Enhanced carousel component with better mobile defaults
 * and platform-specific configurations
 */
export const MobileCarousel = React.forwardRef<
  HTMLDivElement,
  MobileCarouselProps
>(({ 
  children, 
  className,
  opts,
  mobileOpts,
  desktopOpts,
  ...props 
}, ref) => {
  // Merge options based on platform
  const carouselOptions = React.useMemo(() => {
    const baseOptions = {
      align: "start" as const,
      loop: false,
      dragFree: true,
      containScroll: "trimSnaps" as const,
      ...opts,
    };

    if (PlatformService.isNative()) {
      return {
        ...baseOptions,
        // Mobile-specific options
        skipSnaps: false,
        dragThreshold: 10, // Lower threshold for better responsiveness
        inViewThreshold: 0.7, // Better snap behavior
        ...mobileOpts,
      };
    }

    return {
      ...baseOptions,
      ...desktopOpts,
    };
  }, [opts, mobileOpts, desktopOpts]);

  return (
    <Carousel
      ref={ref}
      opts={carouselOptions}
      className={cn(
        className,
        // Add touch-specific classes on mobile
        PlatformService.isNative() && "touch-pan-y"
      )}
      {...props}
    >
      {children}
    </Carousel>
  );
});

MobileCarousel.displayName = 'MobileCarousel';